import { useCallback, useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import toast from 'react-hot-toast';

import Iconify from 'src/components/iconify';
import { useResponsive } from 'src/hooks/useResponsive';
import { chainName, getTargetName } from 'src/utils/crypto/networks';
import { useEthereum } from 'src/hooks/useEthereum';

function getSwitchErrorMessage(error) {
  const text = String(error?.message || error || '').toLowerCase();

  if (text.includes('user rejected') || text.includes('rejected the request')) {
    return 'Network switch was cancelled.';
  }

  if (text.includes('already pending') || text.includes('request already pending')) {
    return 'A MetaMask request is already open. Check MetaMask.';
  }

  if (text.includes('reconnecting')) {
    return 'Wallet is reconnecting. Please wait.';
  }

  if (text.includes('connect metamask first')) {
    return 'Connect MetaMask first.';
  }

  return error?.message || 'Failed to switch network.';
}
export default function NetworkStatusBanner() {
  const theme = useTheme();
  const isMobile = useResponsive('down', 'sm');

  const {
    connected,
    walletChainId,
    targetChainId,
    isSwitchingNetwork,
    isReconnecting,
    switchToTargetNetwork,
  } = useEthereum();

  const targetName = getTargetName();
  const walletName = walletChainId != null ? chainName(walletChainId) : 'Unknown';

  const walletConnected = Boolean(connected);

  const hasMismatch =
    walletConnected &&
    walletChainId != null &&
    targetChainId != null &&
    Number(walletChainId) !== Number(targetChainId);

  const [keepVisible, setKeepVisible] = useState(false);
  const isPending = isSwitchingNetwork || isReconnecting;

  useEffect(() => {
    if (hasMismatch) {
      setKeepVisible(true);
      return;
    }

    if (!isPending) {
      setKeepVisible(false);
    }
  }, [hasMismatch, isPending]);

  const showBanner = hasMismatch || (isPending && keepVisible);

  const onSwitch = useCallback(async () => {
    if (!targetChainId || isPending) return;

    try {
      await toast.promise(switchToTargetNetwork(), {
        loading: `Switching to ${targetName}...`,
        success: `Connected to ${targetName}.`,
        error: (err) => getSwitchErrorMessage(err),
      });
    } catch (error) {
      console.error('[NetworkStatusBanner] switch failed:', error);
    }
  }, [targetChainId, isPending, switchToTargetNetwork, targetName]);

  return (
    <Collapse in={showBanner} unmountOnExit>
      <Box sx={{ mb: 1.5, pb: 1.5 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            gap: 1.25,
            px: { xs: 1.25, sm: 1.5 },
            py: 1.1,
            borderRadius: 2.5,
            border: `1px solid ${alpha(theme.palette.warning.main, 0.28)}`,
            bgcolor: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.14 : 0.08),
          }}
        >
          <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.5,
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
                bgcolor: alpha(
                  theme.palette.warning.main,
                  theme.palette.mode === 'dark' ? 0.2 : 0.14
                ),
                color: theme.palette.warning.main,
              }}
            >
              <Iconify icon="solar:danger-triangle-bold" width={16} />
            </Box>

            <Stack spacing={0.15} sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={800}>
                Wrong network
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: 'block',
                  whiteSpace: isMobile ? 'normal' : 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                App target: <b>{targetName}</b> • Wallet: <b>{walletName}</b>
              </Typography>
            </Stack>
          </Stack>

          <Button
            fullWidth={isMobile}
            size="small"
            variant="contained"
            color="warning"
            onClick={onSwitch}
            disabled={isPending}
            startIcon={<Iconify icon="mdi:swap-horizontal" />}
            sx={{
              flexShrink: 0,
              width: { xs: '100%', sm: 168 },
              minWidth: { xs: '100%', sm: 168 },
              height: 40,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 800,
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' },
            }}
          >
            {isPending ? 'Switching…' : 'Switch network'}
          </Button>
        </Box>
      </Box>
    </Collapse>
  );
}
